import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiPause, FiPlay } from "react-icons/fi";
import {
  PiLeafLight,
  PiProhibitLight,
  PiHeartLight,
  PiPlantLight,
} from "react-icons/pi";
import "animate.css";

const outlets = [
  {
    name: "GreeNox, Ashoka University",
    url: "https://greenox.dotpe.in/store/13/delivery",
    latitude: 28.9445,
    longitude: 77.0967,
  },
  {
    name: "GreeNox, Grub Square, Shiv Nadar University",
    url: "https://greenox.dotpe.in/store/15/delivery",
    latitude: 28.5263,
    longitude: 77.5747,
  },
  {
    name: "GreeNox, Shiv Nadar University",
    url: "https://greenox.dotpe.in/store/16/delivery",
    latitude: 28.5263,
    longitude: 77.5747,
  },
  {
    name: "GreeNox, Christ University",
    url: "https://greenox.dotpe.in/store/1/delivery",
    latitude: 28.6519,
    longitude: 77.4381,
  },
  {
    name: "GreeNox, Bennett University",
    url: "https://greenox.petpooja.com/menu",
    latitude: 28.4491,
    longitude: 77.5838,
  },
  {
    name: "GreeNox, BML Munjal University",
    url: "https://greenox.dotpe.in/store/18/delivery",
    latitude: 28.3226,
    longitude: 76.9365,
  },
];

const highlights = [
  { label: "Freshly prepared", Icon: PiLeafLight },
  { label: "No preservatives", Icon: PiProhibitLight },
  { label: "Wholesome choices", Icon: PiPlantLight },
  { label: "Made for everyday", Icon: PiHeartLight },
];

const getDistanceInKm = (latitude1, longitude1, latitude2, longitude2) => {
  const earthRadiusInKm = 6371;
  const latitudeDifference = ((latitude2 - latitude1) * Math.PI) / 180;
  const longitudeDifference = ((longitude2 - longitude1) * Math.PI) / 180;
  const haversine =
    Math.sin(latitudeDifference / 2) ** 2 +
    Math.cos((latitude1 * Math.PI) / 180) *
      Math.cos((latitude2 * Math.PI) / 180) *
      Math.sin(longitudeDifference / 2) ** 2;

  return (
    2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine)) *
    earthRadiusInKm
  );
};

export default function HeroSection() {
  const videoRef = useRef(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);
  const [selectedOutlet, setSelectedOutlet] = useState("");
  const [isOutletPickerOpen, setIsOutletPickerOpen] = useState(false);
  const [availableOutlets, setAvailableOutlets] = useState(outlets);

  useEffect(() => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const nearbyOutlets = outlets.filter(
          (outlet) =>
            getDistanceInKm(
              coords.latitude,
              coords.longitude,
              outlet.latitude,
              outlet.longitude
            ) <= 3
        );

        if (nearbyOutlets.length > 0) {
          setAvailableOutlets(nearbyOutlets);
        }
      },
      () => {},
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
    );
  }, []);

  const selectedOutletDetails = outlets.find(
    (outlet) => outlet.url === selectedOutlet
  );

  const closeOutletPicker = () => {
    setIsOutletPickerOpen(false);
    setSelectedOutlet("");
  };

  const toggleVideo = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play();
      setIsVideoPlaying(true);
    } else {
      video.pause();
      setIsVideoPlaying(false);
    }
  };

  const handleOutletContinue = () => {
    if (!selectedOutletDetails) return;

    const isMobile = window.matchMedia("(max-width: 767px)").matches;

    if (isMobile) {
      window.location.assign(selectedOutletDetails.url);
    } else {
      window.open(
        selectedOutletDetails.url,
        "_blank",
        "noopener,noreferrer"
      );
    }
  };

  return (
    <section className="relative isolate overflow-hidden bg-stone-800 pt-16">
      {/* Background Video */}
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        poster="/img/greenox-poster.webp"
        className="absolute inset-0 -z-20 h-full w-full object-cover"
      >
        <source src={encodeURI("/img/greenox.mp4")} type="video/mp4" />
        <source src={encodeURI("/img/greenox.webm")} type="video/webm" />
      </video>
      {/* dark gradient on the left so the copy stays readable over the video */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-black/80 via-black/50 to-black/10" />
      <div className="absolute inset-x-0 bottom-0 -z-10 h-1/3 bg-gradient-to-t from-black/50 to-transparent" />

      {/* Overlay Content */}
      <div className="mx-auto flex min-h-[560px] max-w-7xl flex-col justify-center px-5 py-14 sm:px-8 md:min-h-[calc(100vh-4rem)] lg:px-12">
        <div className="max-w-2xl">
          <h1 className="animate__animated animate__fadeInUp text-4xl font-extrabold uppercase leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl xl:text-7xl">
            You are
            <span className="block">what you eat.</span>
            <span className="block text-green-300">
              So make it <span className="normal-case">GreeNox.</span>
            </span>
          </h1>
          <p className="animate__animated animate__fadeInUp mt-5 max-w-md text-lg leading-snug text-white/90 sm:text-xl">
            Fresh bowls, salads, wraps, subs, pasta and more, made for real
            life.
          </p>

          <div className="animate__animated animate__fadeInUp mt-8 flex flex-col gap-3 sm:flex-row sm:gap-4">
            <button
              type="button"
              onClick={() => setIsOutletPickerOpen(true)}
              className="inline-flex h-14 items-center justify-center gap-3 rounded-full border-2 border-green-200/70 bg-green-800/75 px-9 text-base font-semibold text-white shadow-lg backdrop-blur-sm transition hover:bg-green-700/90"
            >
              Order Now
              <FiArrowRight aria-hidden="true" />
            </button>
            <Link
              to="/menu"
              className="inline-flex h-14 items-center justify-center rounded-full bg-white px-9 text-base font-semibold text-gray-900 shadow-lg transition hover:bg-gray-100"
            >
              Explore Menu
            </Link>
          </div>
        </div>

        <ul className="animate__animated animate__fadeIn mt-12 grid max-w-3xl grid-cols-2 gap-x-6 gap-y-4 sm:flex sm:flex-wrap sm:gap-x-8">
          {highlights.map(({ label, Icon }) => (
            <li key={label} className="flex items-center gap-3 text-sm text-white">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/70">
                <Icon size={20} aria-hidden="true" />
              </span>
              {label}
            </li>
          ))}
        </ul>
      </div>

      <button
        type="button"
        onClick={toggleVideo}
        aria-label={isVideoPlaying ? "Pause background video" : "Play background video"}
        className="absolute bottom-6 right-6 flex h-11 w-11 items-center justify-center rounded-full border border-white/70 bg-black/30 text-white backdrop-blur transition hover:bg-black/50"
      >
        {isVideoPlaying ? <FiPause aria-hidden="true" /> : <FiPlay aria-hidden="true" />}
      </button>

      {isOutletPickerOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          role="presentation"
          onClick={closeOutletPicker}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-6 text-left shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="outlet-picker-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h2
              id="outlet-picker-title"
              className="text-xl font-bold text-gray-900"
            >
              Choose your outlet
            </h2>
            <p className="mt-2 text-gray-600">
              Select the GreeNox outlet you want to order from.
            </p>
            <div className="mt-5 flex max-h-72 flex-col gap-2 overflow-y-auto">
              {availableOutlets.map((outlet) => (
                <button
                  key={outlet.url}
                  type="button"
                  onClick={() => setSelectedOutlet(outlet.url)}
                  aria-pressed={selectedOutlet === outlet.url}
                  className={`rounded-xl border px-4 py-3 text-left font-medium transition ${
                    selectedOutlet === outlet.url
                      ? "border-green-700 bg-green-50 text-green-800"
                      : "border-gray-200 text-gray-800 hover:border-green-600"
                  }`}
                >
                  {outlet.name}
                </button>
              ))}
            </div>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closeOutletPicker}
                className="rounded-full border border-gray-300 px-5 py-3 font-semibold text-gray-700 transition hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleOutletContinue}
                disabled={!selectedOutletDetails}
                className="rounded-full bg-green-800 px-5 py-3 font-semibold text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Continue to order
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
